/* Space Age Design Tokens */
/* Import shared tokens - single source of truth for fonts and CSS variables */
@import './css-variables.css' layer(base);
@import './primitives.css' layer(base);

@import 'tailwindcss';

@config '../../tailwind.config.js';

/* Only semantic, token-backed type sizes may compile. */
@theme { --text-*: initial; }
@utility text-eyebrow { font-size: var(--font-eyebrow); }

/* The v3 container configuration is no longer consumed by v4. */
@utility container {
  margin-inline: auto;
  padding-inline: 2rem;
  @media (width >= 96rem) { max-width: 1400px; }
}

/*
  The default border color has changed to `currentcolor` in Tailwind CSS v4,
  so we've added these compatibility styles to make sure everything still
  looks the same as it did with Tailwind CSS v3.

  If we ever want to remove these styles, we need to add an explicit border
  color utility to any element that depends on these defaults.
*/
@layer base {
  *,
  ::after,
  ::before,
  ::backdrop,
  ::file-selector-button {
    border-color: var(--color-gray-200, currentcolor);
  }
}

@utility text-display {
  /* Typography Component Classes */
  font-family: var(--font-display-family);
  font-size: var(--font-display);
  line-height: var(--font-display-line);
  font-weight: 700;
  letter-spacing: 0;
}

@utility text-h1 {
  font-family: var(--font-display-family);
  font-size: var(--font-h1);
  line-height: var(--font-h1-line);
  font-weight: 700;
  letter-spacing: 0;
}

@utility text-h2 {
  font-family: var(--font-display-family);
  font-size: var(--font-h2);
  line-height: var(--font-h2-line);
  font-weight: 600;
  letter-spacing: 0;
}

@utility text-h3 {
  font-family: var(--font-display-family);
  font-size: var(--font-h3);
  line-height: var(--font-h3-line);
  font-weight: 600;
  letter-spacing: 0;
}

@utility text-h4 {
  font-family: var(--font-sans);
  font-size: var(--font-h4);
  line-height: var(--font-h4-line);
  font-weight: 600;
  letter-spacing: 0;
}

@utility text-h5 {
  font-family: var(--font-sans);
  font-size: var(--font-h5);
  line-height: var(--font-h5-line);
  font-weight: 600;
  letter-spacing: 0;
}

@utility text-body-large {
  font-family: var(--font-sans);
  font-size: var(--font-body-large);
  line-height: var(--font-body-large-line);
  font-weight: 400;
}

@utility text-body-xl {
  font-family: var(--font-sans);
  font-size: var(--font-body-xl);
  line-height: var(--font-body-xl-line);
  font-weight: 400;
}

@utility text-body {
  font-family: var(--font-sans);
  font-size: var(--font-body);
  line-height: var(--font-body-line);
  font-weight: 400;
}

@utility text-body-small {
  font-family: var(--font-sans);
  font-size: var(--font-body-small);
  line-height: var(--font-body-small-line);
  font-weight: 400;
}

@utility text-body-xs {
  font-family: var(--font-sans);
  font-size: var(--font-body-xs);
  line-height: var(--font-body-xs-line);
  font-weight: 400;
}

/* Labels use the sans in normal case; monospace is for data (text-meta, text-badge-count). */
@utility text-caption {
  font-family: var(--font-sans);
  font-size: var(--font-caption);
  line-height: var(--font-caption-line);
  font-weight: 500;
}

@utility text-micro {
  font-family: var(--font-sans);
  font-size: var(--font-micro);
  line-height: var(--font-micro-line);
  font-weight: 600;
  letter-spacing: 0.02em;
}

@utility text-badge-count {
  font-family: var(--font-mono);
  font-size: var(--font-badge-count);
  line-height: var(--font-badge-count-line);
  font-weight: 700;
  letter-spacing: 0.05em;
}

@utility text-overlay-ribbon-label {
  font-family: var(--font-sans);
  font-size: var(--font-overlay-ribbon-label);
  line-height: var(--font-overlay-ribbon-line);
  font-weight: 600;
}

@utility text-overlay-ribbon-label-compact {
  font-size: var(--font-overlay-ribbon-label-compact);
}

@utility text-overlay-ribbon-title {
  font-family: var(--font-sans);
  font-size: var(--font-overlay-ribbon-title);
  line-height: var(--font-overlay-ribbon-title-line);
  font-weight: 700;
}

@utility text-quiet-action {
  font-family: var(--font-sans);
  font-size: var(--font-caption);
  line-height: var(--font-caption-line);
  font-weight: 600;
}

@utility text-button {
  font-size: var(--font-button);
  line-height: var(--font-button-line);
}

@utility control-height {
  /* Form control sizing */
  height: var(--control-height);
  min-height: var(--control-height);
}

@utility film-strip-border {
  /* Film strip decorative element (disabled) */
  position: relative;
}

@utility spotlight-glow {
  /* Spotlight effect (disabled) */
  position: relative;
}

@utility text-meta {
  /* Typography Utility Classes */
  font-family: var(--font-mono);
  font-weight: 500;
  letter-spacing: 0.02em;
}

@utility text-label {
  font-family: var(--font-sans);
  font-weight: 600;
  letter-spacing: 0.02em;
}

@utility text-label-light {
  font-family: var(--font-sans);
  letter-spacing: 0.01em;
}

@utility text-secondary-readable {
  color: hsl(var(--secondary-readable));
}

@layer base {
  /* Base Typography Styles */
  html {
    font-feature-settings: "rlig" 1, "calt" 1;
  }

  body {
    margin: 0;
    font-family: var(--font-sans);
    font-size: var(--font-body);
    line-height: var(--font-body-line);
    color: hsl(var(--foreground));
    background-color: hsl(var(--background));
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
  }

  code {
    font-family: var(--font-mono);
  }

  /* Focus Styles */
  *:focus-visible {
    outline: 2px solid hsl(var(--ring));
    outline-offset: 2px;
  }

  /* Scrollbar Styles */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: hsl(var(--muted));
  }

  ::-webkit-scrollbar-thumb {
    background: hsl(var(--muted-foreground));
    border-radius: var(--radius);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: hsl(var(--foreground) / 0.7);
  }
}
